import type { PublicTenantConfig, TenantTypography, TokenColors, TypographyStep } from './types'

export function tenantTokensStyleBlock(tenant: PublicTenantConfig): string | null {
    if (!tenant.tokens) {
        return null
    }

    const declarations = tokenColorsToCssDeclarations(tenant.tokens.colors)
    return `:root[data-tenant='${tenant.id}'] {\n${declarations}\n}`
}

export function headingScaleVariables(
    scale: TenantTypography['scale'],
    buildCustomVars?: (level: string, step: TypographyStep, out: Record<string, string>) => Record<string, string>,
): Record<string, string> {
    if (!scale) return {}
    return Object.entries(scale).reduce<Record<string, string>>((out, [level, step]) => {
        out[`--gm-heading-${level}-font-size`] = step.fontSize
        out[`--gm-heading-${level}-line-height`] = step.lineHeight

        return buildCustomVars ? buildCustomVars(level, step, out) : out
    }, {})
}

function tokenColorsToCssDeclarations(colors: TokenColors): string {
    const { brand, highlight, gradient } = colors

    const highlightForeground = highlight?.foreground ?? brand[6]
    const highlightBackground = highlight?.background ?? brand[6]
    const highlightBackgroundSubtle = highlight?.backgroundSubtle ?? `color-mix(in srgb, ${brand[6]} 10%, transparent)`
    const gradientHighlightFrom = gradient?.highlightFrom ?? brand[4]

    const brandRamp = Object.fromEntries(
        brand.flatMap((value, index) => [
            [`--gm-color-brand-${index}`, value],
            // Aliasing the blue ramp is what lets a reskin reach the existing
            // var(--gm-blue-*) call sites without editing them.
            [`--gm-blue-${index}`, value],
        ]),
    )

    const properties: Record<string, string> = {
        ...brandRamp,
        '--gm-blue': highlightBackground,

        // The legacy name is General Medicine's light ramp stop (#b8ccff), not the semantic highlight.
        '--gm-fg-highlight': brand[2],
        '--gm-bg-highlight': highlightBackground,
        '--gm-bg-highlight-cont': highlightBackgroundSubtle,

        '--gm-foreground-fg-highlight': highlightForeground,
        '--gm-background-bg-highlight': highlightBackground,
        '--gm-background-bg-highlight-container': highlightBackgroundSubtle,

        '--gm-gradient-highlight-from': gradientHighlightFrom,
    }

    return Object.entries(properties)
        .map(([name, value]) => `  ${name}: ${value};`)
        .join('\n')
}
